import { cn } from "@/lib/utils";
import { NvBadge } from "./nv-badge";
import { NvBookmarkButton } from "./nv-bookmark-button";
import { NvCardLink } from "./nv-card-link";
import { NvStarIcon } from "./nv-icons";
import { NvMonogramTile, type NvMonogramTone } from "./nv-monogram-tile";
import { NvPill } from "./nv-pill";
import { NvTagPill, type NvTagValue } from "./nv-tag-pill";

// Agent cards: Figma "Card / Top 10 Item" (185:4467 · HOME), "Card / Agent Result"
// (203:13649 · Agent Finder) and "Card / Agent Vertical" (139:23206 · My Page).

const cardClass = "relative rounded-nv-card border border-nv-border bg-nv-surface";

export interface NvAgentMetric {
  label: string;
  value: string;
}

interface AgentLogoProps {
  name: string;
  logoUrl?: string;
  initial?: string;
  size: 48 | 56;
  tone: NvMonogramTone;
}

function AgentLogo({ name, logoUrl, initial, size, tone }: AgentLogoProps) {
  if (logoUrl) {
    return <img src={logoUrl} alt="" className={cn("shrink-0 rounded-nv-control object-cover", size === 56 ? "h-14 w-14" : "h-12 w-12")} />;
  }
  return <NvMonogramTile name={name} initial={initial} size={size} tone={tone} />;
}

export interface NvTop10CardProps {
  rank: number;
  name: string;
  summary: string;
  rating?: number;
  // 괄호 숫자 — 평점이 없으면 숫자만 "(480)" (HOME 산업별 Top 10은 조회 수, 가정 A12)
  reviewCount?: number;
  to?: string;
  linkState?: unknown;
  className?: string;
}

function NvTop10Card({ rank, name, summary, rating, reviewCount, to, linkState, className }: NvTop10CardProps) {
  return (
    <div className={cn(cardClass, "flex gap-4 px-5 py-[18px]", className)}>
      <span className="w-6 shrink-0 font-num text-[20px] font-medium leading-6 text-nv-text-primary">{rank}</span>

      <div className="flex min-w-0 flex-1 flex-col gap-[6px]">
        <div className="flex items-center gap-2">
          <NvCardLink to={to} state={linkState} className="truncate text-[15px] font-medium leading-[18px] text-nv-text-primary">
            {name}
          </NvCardLink>
          {(rating !== undefined || reviewCount !== undefined) && (
            <span className="inline-flex shrink-0 items-center gap-1 text-[12px] leading-[14px] text-nv-text-secondary">
              {rating !== undefined && (
                <>
                  <NvStarIcon size={14} />
                  {rating.toFixed(1)}
                </>
              )}
              {reviewCount !== undefined && `(${reviewCount.toLocaleString("ko-KR")})`}
            </span>
          )}
        </div>
        <p className="truncate text-[12px] leading-[18px] text-nv-text-secondary">{summary}</p>
      </div>
    </div>
  );
}

export interface NvAgentResultCardProps {
  name: string;
  summary: string;
  logoUrl?: string;
  logoInitial?: string;
  logoTone?: NvMonogramTone;
  certified?: boolean;
  // Agent type · deployment pills, e.g. ["단일 에이전트", "커스터마이징 필요"]; with a definition they show a tooltip
  tags?: (string | NvTagValue)[];
  topMatch?: boolean;
  // 컨셉추얼 구분 뱃지 (IA 65, PwC 내부에게만 보이는 에이전트)
  conceptual?: boolean;
  // e.g. "산업재·제조 · 건설 / 운영 · 설비관리·예지보전"
  category?: string;
  metric?: NvAgentMetric;
  bookmarked?: boolean;
  onBookmarkToggle?: (next: boolean) => void;
  to?: string;
  linkState?: unknown;
  className?: string;
}

function NvAgentResultCard({
  name,
  summary,
  logoUrl,
  logoInitial,
  logoTone = "dark",
  certified = false,
  tags = [],
  topMatch = false,
  conceptual = false,
  category,
  metric,
  bookmarked,
  onBookmarkToggle,
  to,
  linkState,
  className,
}: NvAgentResultCardProps) {
  return (
    <div className={cn(cardClass, "flex gap-5 p-6", className)}>
      <AgentLogo name={name} logoUrl={logoUrl} initial={logoInitial} size={56} tone={logoTone} />

      <div className="flex min-w-0 flex-1 flex-col gap-[6px]">
        <div className="flex flex-wrap items-center gap-2">
          <NvCardLink to={to} state={linkState} className="font-num text-[20px] font-medium leading-6 text-nv-text-primary">
            {name}
          </NvCardLink>
          {certified && <NvBadge>PwC Certified</NvBadge>}
          {tags.map((tag) =>
            typeof tag === "string" ? <NvPill key={tag}>{tag}</NvPill> : <NvTagPill key={tag.label} label={tag.label} definition={tag.definition} />,
          )}
          {conceptual && <NvPill>컨셉추얼</NvPill>}
          {topMatch && (
            <NvPill tone="highlight" className="gap-1 border border-nv-brand">
              <NvStarIcon size={13} />
              상위 일치
            </NvPill>
          )}
        </div>
        {category && <p className="text-[12px] leading-[14px] text-nv-text-secondary">{category}</p>}
        <p className="line-clamp-2 text-[14px] leading-[22px] text-nv-text-primary">{summary}</p>
        {metric && (
          <div className="flex items-center gap-6 pt-1">
            <span className="text-[12px] leading-[14px] text-nv-text-secondary">{metric.label}</span>
            <span className="font-num text-[15px] font-medium leading-[18px] text-nv-success">{metric.value}</span>
          </div>
        )}
      </div>

      {bookmarked !== undefined && (
        <NvBookmarkButton bookmarked={bookmarked} onToggle={onBookmarkToggle} size="md" className="self-start" />
      )}
    </div>
  );
}

export interface NvAgentVerticalCardProps {
  name: string;
  // 공급사 (설계서 v0.4: 카드에 공급사 추가 · 지표 삭제)
  supplier?: string;
  summary: string;
  logoUrl?: string;
  logoInitial?: string;
  logoTone?: NvMonogramTone;
  certified?: boolean;
  // 위쪽 줄: 로고와 이 자리(북마크 별 · 선택 체크 + 삭제)가 양끝에 놓여요. 로고 위치로 어느 쪽인지 정해요.
  logoPosition?: "left" | "right";
  action?: React.ReactNode;
  to?: string;
  className?: string;
}

// Figma ver.6 "Card / Agent (My Page)": 282 x 261, r12, 안쪽 20 · 간격 10 — [로고 48 | 별 · 체크] · 이름 17/20 · 공급사 13/20 · 배지 · 한 줄 소개 13/20(2줄)
function NvAgentVerticalCard({
  name,
  supplier,
  summary,
  logoUrl,
  logoInitial,
  logoTone = "dark",
  certified = false,
  logoPosition = "left",
  action,
  to,
  className,
}: NvAgentVerticalCardProps) {
  const logo = <AgentLogo name={name} logoUrl={logoUrl} initial={logoInitial} size={48} tone={logoTone} />;
  return (
    <div className={cn(cardClass, "flex flex-col gap-[10px] p-5", className)}>
      <div className={cn("flex items-start justify-between", logoPosition === "right" && "flex-row-reverse")}>
        {logo}
        {action}
      </div>

      <NvCardLink to={to} className="text-[17px] font-medium leading-5 text-nv-text-primary">
        {name}
      </NvCardLink>
      <p className="line-clamp-2 min-h-10 text-[13px] leading-5 text-nv-text-secondary">{supplier}</p>
      {certified && <NvBadge className="self-start">PwC Certified</NvBadge>}
      <p className="line-clamp-2 text-[13px] leading-5 text-nv-text-secondary">{summary}</p>
    </div>
  );
}

export { NvTop10Card, NvAgentResultCard, NvAgentVerticalCard };
